import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Restore as RestoreIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DescountService from '../../services/descount.service';
import { useTranslation } from '../../hooks/useTranslation';

const DeletedDescountsList = () => {
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const [descounts, setDescounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [restoreDialog, setRestoreDialog] = useState({ open: false, id: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();

  useEffect(() => {
    fetchDeletedDescounts();
  }, []);

  const fetchDeletedDescounts = async () => {
    setLoading(true);
    try {
      const response = await DescountService.getTrashed();
      setDescounts(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching deleted descounts:', error);
      showSnackbar(t('descounts.errorLoading') + ': ' + (error.response?.data?.message || error.message), 'error');
      setDescounts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = (id) => {
    setRestoreDialog({ open: true, id });
  };

  const confirmRestore = async () => {
    try {
      await DescountService.restore(restoreDialog.id);
      showSnackbar(t('descounts.restoredSuccess'));
      fetchDeletedDescounts();
    } catch (error) {
      showSnackbar(t('descounts.errorRestoring'), 'error');
    } finally {
      setRestoreDialog({ open: false, id: null });
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredDescounts = descounts.filter(d =>
    d.porcentaje?.toString().includes(searchTerm) ||
    d.monto?.toString().includes(searchTerm)
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <IconButton onClick={() => navigate('/descounts')}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5">{t('descounts.deletedDescounts')}</Typography>
            </Box>
          </Box>

          <TextField
            placeholder={t('descounts.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ mb: 2, minWidth: 300 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('descounts.percentage')}</TableCell>
                  <TableCell>{t('descounts.amount')}</TableCell>
                  <TableCell>{t('descounts.deletedAt')}</TableCell>
                  <TableCell align="center">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredDescounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      {t('descounts.noDeletedFound')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDescounts.map((descount) => (
                    <TableRow key={descount.id} hover>
                      <TableCell>{descount.porcentaje}%</TableCell>
                      <TableCell>{descount.monto}</TableCell>
                      <TableCell>{formatDate(descount.deleted_at)}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {hasPermission('descuento.create') && (
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleRestore(descount.id)}
                              title={t('descounts.restoreDescount')}
                            >
                              <RestoreIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog
        open={restoreDialog.open}
        onClose={() => setRestoreDialog({ open: false, id: null })}
      >
        <DialogTitle>{t('descounts.restoreDescount')}</DialogTitle>
        <DialogContent>
          <Typography>{t('descounts.restoreConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialog({ open: false, id: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmRestore} color="primary" variant="contained">
            {t('descounts.restore')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DeletedDescountsList;
